import type { BackgroundVariant, BorderVariant, TextVariant, ThemeColours } from "@qendo/theme";
import base from "./base";

const text: Record<TextVariant, string> = {
    primary: base.slate["100"],
    secondary: base.slate["400"],
    tertiary: base.slate["500"],
    quaternary: base.slate["500"],
    info: base.navy["300"],
    success: base.green["400"],
    warning: base.yellow["400"],
    danger: base.red["400"],
    selected: base.navy["300"],
    neutral: base.slate["400"],
    brand: base.navy["400"],
    "inverse-primary": base.slate["900"],
    "inverse-secondary": base.slate["700"],
    link: base.navy["200"],
    "link-pressed": base.navy["300"],
    "link-disabled": base.slate["500"],
};
const icon = text;

const border: Record<BorderVariant, string> = {
    primary: base.slate["700"],
    secondary: base.slate["800"],
    tertiary: base.slate["600"],
    quaternary: base.slate["500"],
    info: base.navy["300"],
    success: base.green["700"],
    warning: base.yellow["700"],
    danger: base.red["400"],
    selected: base.navy["400"],
    neutral: base.slate["800"],
    brand: base.navy["700"],
};

const background: Record<BackgroundVariant, string> = {
    info: base.navy["800"],
    success: base.green["900"],
    warning: base.yellow["900"],
    danger: base.red["900"],
    brand: base.navy["800"],
    primary: base.slate["800"],
    "primary-pressed": base.slate["800"],
    secondary: base.slate["900"],
    "secondary-pressed": base.slate["700"],
    tertiary: base.slate["700"],
    transparent: base.transparent,
    "gradient-start": "#283C72CC",
    "gradient-end": "#162D5900",
};

export const theme: ThemeColours = {
    brand: border.brand,
    brandHighlight: text.brand,
    boxShadow: "#0b0d10cc",
    backdrop: "rgba(20, 30, 102, 0.5)", // base.blue["900"]/50% opacity
    text,
    icon,
    border,
    background,
    button: {
        primary: text["inverse-primary"],
        "primary-pressed": text["inverse-primary"],
        "primary-disabled": text.tertiary,
        "primary-background": base.navy["200"],
        "primary-pressed-background": base.navy["300"],
        "primary-disabled-background": base.slate["800"],
        secondary: base.navy["100"],
        "secondary-pressed": base.navy["200"],
        "secondary-disabled": text.tertiary,
        "secondary-background": base.navy["700"],
        "secondary-pressed-background": base.navy["800"],
        "secondary-disabled-background": base.slate["800"],
        quiet: base.navy["300"],
        "quiet-pressed": base.navy["400"],
        "quiet-disabled": text.tertiary,
        "quiet-background": base.transparent,
        "quiet-pressed-background": base.blue["900"],
        "quiet-disabled-background": base.transparent,
        "quiet-inverse": text["inverse-primary"],
        "quiet-inverse-pressed": text["inverse-primary"],
        "quiet-inverse-disabled": text.tertiary,
        "quiet-inverse-background": base.transparent,
        "quiet-inverse-pressed-background": base.transparent,
        "quiet-inverse-disabled-background": base.transparent,
        neutral: base.navy["300"],
        "neutral-pressed": base.navy["400"],
        "neutral-disabled": text.tertiary,
        "neutral-background": base.slate["800"],
        "neutral-pressed-background": base.slate["700"],
        "neutral-disabled-background": base.slate["900"],
        "neutral-quiet": base.navy["300"],
        "neutral-quiet-pressed": base.navy["400"],
        "neutral-quiet-disabled": text.tertiary,
        "neutral-quiet-background": base.transparent,
        "neutral-quiet-pressed-background": base.transparent,
        "neutral-quiet-disabled-background": base.transparent,
        destructive: base.red["300"],
        "destructive-pressed": base.red["400"],
        "destructive-disabled": text.tertiary,
        "destructive-background": base.red["900"],
        "destructive-pressed-background": base.red["700"],
        "destructive-disabled-background": base.slate["800"],
        "destructive-quiet": base.red["400"],
        "destructive-quiet-pressed": base.red["400"],
        "destructive-quiet-disabled": text.tertiary,
        "destructive-quiet-background": base.transparent,
        "destructive-quiet-pressed-background": base.transparent,
        "destructive-quiet-disabled-background": base.transparent,
        warning: text.warning,
        "warning-pressed": text.warning,
        "warning-disabled": text.tertiary,
        "warning-background": base.yellow["900"],
        "warning-pressed-background": base.yellow["700"],
        "warning-disabled-background": base.slate["700"],
        "warning-quiet": text.warning,
        "warning-quiet-pressed": text.warning,
        "warning-quiet-disabled": text.tertiary,
        "warning-quiet-background": base.transparent,
        "warning-quiet-pressed-background": base.transparent,
        "warning-quiet-disabled-background": base.transparent,
        success: text.success,
        "success-pressed": text.success,
        "success-disabled": text.tertiary,
        "success-background": base.green["900"],
        "success-pressed-background": base.green["700"],
        "success-disabled-background": base.slate["700"],
        "success-quiet": text.success,
        "success-quiet-pressed": text.success,
        "success-quiet-disabled": text.tertiary,
        "success-quiet-background": base.transparent,
        "success-quiet-pressed-background": base.transparent,
        "success-quiet-disabled-background": base.transparent,
    },
    toggle: {
        indicator: base.slate["900"],
        disabled: base.slate["800"],
        off: base.slate["600"],
        on: base.navy["400"],
    },
    check: {
        selector: base.navy["400"],
        background: base.navy["900"],
        disabled: text.tertiary,
    },
    toast: {
        background: base.navy["800"],
        foreground: base.navy["100"],
    },
    tracking: {
        indicator: base.navy["200"],
        selected: base.navy["300"],
        "past-period": base.navy["700"],
        info: base.navy["300"],
        current: base.navy["300"],
        prediction: base.slate["600"],
        feature: base.navy["300"],
        unselected: base.navy["700"],
        "gradient-top": base.navy["300"],
        "gradient-bottom": base.navy["700"],
    },
    input: {
        background: background.primary,
        border: border.primary,
        text: text.primary,
        placeholder: text.tertiary,
        icon: icon.secondary,
        "background-disabled": background.secondary,
        "text-disabled": text.secondary,
        "icon-disabled": icon.tertiary,
        "border-disabled": border.secondary,
        "border-focus": border.selected,
        "border-error": border.danger,
    },
    tabs: {
        background: base.navy["900"],
        selected: base.navy["300"],
    },
    reports: [
        text.brand,
        base.green["300"],
        base.yellow["400"],
        base.red["300"],
        base.pink["200"],
        base.slate["500"],
        base.blue["200"],
        base.green["200"],
        base.yellow["200"],
        base.red["100"],
    ],
};

export default theme;
